'use client';
import { useCategoryFilters } from '@util/hooks/useCategoryFilters';
import { useFitmentFilterSync } from '@util/hooks/useFitmentFilterSync';
import useProductsPage from '@util/hooks/useProductsPage';
import MobileSearchFilters from 'app/search/components/MobileSearchFilters';
import MobileSearchSort from 'app/search/components/MobileSearchSort';
import SearchControls from 'app/search/components/SearchControls';
import { useBikeFilter } from 'context/BikeFilterContext';
import { Bike } from 'models/shared';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { Suspense, useCallback, useRef, useState } from 'react';
import { CategoryOrSubcategoryPageProps } from './CategoryOrSubcategoryPage';
import Filters from './filters/Filters';
import FloatingMobileFilter from './FloatingMobileFilter';
import CategorySkeleton from './skeletons/CategorySkeleton';
import DynamicSkeleton from './skeletons/DynamicSkeleton';

const PageMainContent = dynamic(() => import('./products/PageMainContent'), {
  ssr: true,
  loading: () => <CategorySkeleton />,
});

const ShopVehicleWrapper = dynamic(() => import('./ShopVehicleWrapper'), {
  ssr: false,
  loading: () => <DynamicSkeleton height={81} />,
});

export default function CategoryOrSubcategoryPageClient(
  props: CategoryOrSubcategoryPageProps
) {
  const {
    current_cat,
    keyword,
    categories,
    category,
    category1,
    category2,
    category3,
    results,
    on_sale,
    pageInfo: initialPageInfo,
    initialTopFilters,
    initialSideFilters,
    brands,
    facet_counts,
    color,
    mostly_parts,
  } = props;

  const searchControlsRef = useRef<HTMLDivElement>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);

  const {
    buttonToggles,
    sideFilters,
    topFilters,
    handleSideFiltersChange,
    handleSortChange,
    setDrawerOpen,
    isFetching,
    setIsFetching,
    handleSearchChange,
    clearAllFilters,
  } = useProductsPage({
    current_cat,
    results,
    pageInfo: initialPageInfo,
    initialTopFilters,
    initialSideFilters,
    categories,
    category,
    category1,
    category2,
    category3,
    keyword,
    on_sale,
    brands,
    facet_counts,
    color,
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { setSelectedBike, clearSelectedBike, setFitmentFilterEnabled } =
    useBikeFilter();
  const [showMobileEditGarageModal, setShowMobileEditGarageModal] =
    useState(false);

  const onBikeComplete = useCallback(
    (bike: Bike | null) => {
      if (bike === null) {
        handleSideFiltersChange({
          ...sideFilters,
          bikeYears: [],
          bikeMakes: [],
          bikeModels: [],
        });
        clearSelectedBike();
        return;
      }
      if (!bike.year || !bike.make || !bike.model) return;
      handleSideFiltersChange({
        ...sideFilters,
        bikeYears: [`${bike.year}`],
        bikeMakes: [bike.make],
        bikeModels: [bike.model ?? ''],
      });
      setSelectedBike(bike);
    },
    [handleSideFiltersChange, sideFilters, setSelectedBike, clearSelectedBike]
  );

  const onToggleBikeFilter = useCallback(
    (enabled: boolean) => {
      // unchecking keeps the vehicle in the bar so it can be turned back on for
      // the next search without picking it again
      setFitmentFilterEnabled(enabled);

      if (enabled) return;

      handleSideFiltersChange({
        ...sideFilters,
        bikeYears: [],
        bikeMakes: [],
        bikeModels: [],
      });
    },
    [handleSideFiltersChange, sideFilters, setFitmentFilterEnabled]
  );

  const pathname = usePathname() ?? '';

  const {
    handleFilterChange,
    handleCategoryChange,
    handleBikeSelection,
    handleSizeChange,
    handlePriceRangeChange,
    activePage,
    setActivePage,
    pendingFilterChanges,
    setPendingFilterChanges,
  } = useCategoryFilters({
    filterState: sideFilters,
    setFilterState: handleSideFiltersChange,
  });

  const shouldShowShopVehicle =
    !current_cat?.product_cats.category ||
    ['Bike Parts', 'Accessories'].includes(current_cat?.product_cats.category);

  useFitmentFilterSync({
    filters: sideFilters,
    onBikeComplete,
    enabled: shouldShowShopVehicle,
    // category slugs can already pin a make, so a vehicle carried over from
    // somewhere else waits for the shopper to check the box instead of stacking
    // onto the make the url asked for
    autoApply: false,
  });

  return (
    <>
      {shouldShowShopVehicle && (
        <ShopVehicleWrapper
          onBikeComplete={onBikeComplete}
          onToggleBikeFilter={onToggleBikeFilter}
          sideFilters={sideFilters}
          setShowMobileEditGarageModal={setShowMobileEditGarageModal}
          handleSideFiltersChange={handleSideFiltersChange}
          showMobileEditGarageModal={showMobileEditGarageModal}
        />
      )}
      <PageMainContent
        view={topFilters.view}
        isFetching={isFetching}
        setIsFetching={setIsFetching}
        products={results}
        pageInfo={initialPageInfo}
        filterState={sideFilters}
        onFiltersChange={handleSideFiltersChange}
        attribution={{
          data_source: 'typesense',
          page: pathname,
          section: `category-page-${topFilters.view}`,
        }}
        clearAllFilters={clearAllFilters}
        searchControlsRef={searchControlsRef}
        filters={
          <div className="w-[38rem]">
            <Filters
              categories={categories}
              filterState={sideFilters}
              handleFilterChange={handleFilterChange}
              handleCategoryChange={handleCategoryChange}
              handleBikeSelection={handleBikeSelection}
              handleSizeChange={handleSizeChange}
              handlePriceRangeChange={handlePriceRangeChange}
              pendingFilterChanges={pendingFilterChanges}
              setPendingFilterChanges={setPendingFilterChanges}
              mostly_parts={mostly_parts}
              facet_counts={facet_counts}
              pageType={'category'}
              activePage={activePage}
              setActivePage={setActivePage}
            />
          </div>
        }
        controls={
          <SearchControls
            keyword={keyword ?? ''}
            showSearch={false}
            handleConditionsChange={(value) =>
              handleFilterChange('conditions', value)
            }
            handleSellerTypeChange={(value) =>
              handleFilterChange('sellerType', value)
            }
            handleSaleChange={(value) => handleFilterChange('onsale', value)}
            handleSortChange={handleSortChange}
            setDrawerOpen={setDrawerOpen}
            topFilters={topFilters}
            pageType={'category'}
            dropdownOpen={dropdownOpen}
            buttonToggles={buttonToggles}
            handleSearchChange={handleSearchChange}
            setDropdownOpen={setDropdownOpen}
            filterState={sideFilters}
            setFilterState={handleSideFiltersChange}
            currentCat={current_cat}
            facet_counts={facet_counts}
            mostly_parts={mostly_parts}
            categories={categories}
          />
        }
      />
      <Suspense>
        {/* Calculate if there are active filters */}
        {(() => {
          const hasActiveFilters =
            sideFilters.conditions?.length > 0 ||
            sideFilters.brands?.length > 0 ||
            (sideFilters.category && sideFilters.category.length > 0) ||
            (sideFilters.product_cats?.category &&
              sideFilters.product_cats.category.length > 0) ||
            (sideFilters.product_cats?.category1 &&
              sideFilters.product_cats.category1.length > 0) ||
            (sideFilters.product_cats?.category2 &&
              sideFilters.product_cats.category2.length > 0) ||
            (sideFilters.product_cats?.category3 &&
              sideFilters.product_cats.category3.length > 0) ||
            (sideFilters.letterSizes && sideFilters.letterSizes.length > 0) ||
            (sideFilters.numberSizes && sideFilters.numberSizes.length > 0) ||
            (sideFilters.sellerType && sideFilters.sellerType.length > 0) ||
            (sideFilters.bikeYears && sideFilters.bikeYears.length > 0) ||
            (sideFilters.bikeMakes && sideFilters.bikeMakes.length > 0) ||
            (sideFilters.bikeModels && sideFilters.bikeModels.length > 0) ||
            sideFilters.onsale ||
            sideFilters.recentlySold;

          return (
            <FloatingMobileFilter
              filterSectionRef={searchControlsRef}
              onFilterClick={() => setShowMobileFilters(true)}
              onSortClick={() => setShowMobileSort(true)}
              hasActiveFilters={hasActiveFilters}
            />
          );
        })()}
        {/* Mobile filter drawer */}
        <MobileSearchFilters
          showFilters={showMobileFilters}
          setShowFilters={setShowMobileFilters}
          categories={categories}
          filterState={sideFilters}
          setFilterState={handleSideFiltersChange}
          facet_counts={facet_counts}
          mostly_parts={mostly_parts}
          pageType={'category'}
        />

        {/* Mobile sort drawer */}
        <MobileSearchSort
          show={showMobileSort}
          setShow={setShowMobileSort}
          sortingBy={topFilters.sort}
          onSortingChange={handleSortChange}
          pageType={'category'}
          filterState={sideFilters}
        />
      </Suspense>
    </>
  );
}
